import { Injectable } from '@angular/core';
import { Studente } from '../../../../api-clients/generated/services';
import { ParseResult, ParsedToken, ActionType, INTENT_LABELS, isActionType, computeConfidence, formatEuro } from './command-parser.model';
import { tokenize, normalizeLightInput } from './tokenizer';
import { matchIntent } from './intent-matcher';
import { matchChild } from './child-matcher';
import { extractDates, formatDateForDisplay } from './date-parser';
import { extractAmount } from './amount-parser';
import { NluEngineService } from './nlu/nlu-engine.service';

@Injectable({ providedIn: 'root' })
export class CommandParserService {

  constructor(private nluEngine: NluEngineService) {}

  parseLive(input: string, figli: Studente[]): ParseResult {
    return this.doParse(input, figli, false);
  }

  async parse(input: string, figli: Studente[]): Promise<ParseResult> {
    const result = this.doParse(input, figli, true);

    // Level 2: NLU fallback if keyword matching found no intent
    if (!result.intent) {
      await this.tryNluFallback(result);
    }

    return result;
  }

  /**
   * Attempts NLU classification on unconsumed noise tokens and mutates the
   * ParseResult in-place if a valid intent is found. Used by both parse()
   * (on Enter) and the component's live debounce preview.
   */
  async tryNluFallback(result: ParseResult): Promise<boolean> {
    const remainingText = result.tokens
      .filter(t => t.type === 'noise')
      .map(t => t.raw)
      .join(' ')
      .trim();

    if (!remainingText) return false;

    const nluResult = await this.nluEngine.classify(remainingText);
    if (!nluResult.intent || !isActionType(nluResult.intent)) return false;

    result.intent = nluResult.intent;
    result.tokens = result.tokens.filter(t => t.type !== 'noise');
    result.tokens.unshift({
      raw: remainingText,
      type: 'intent',
      resolved: INTENT_LABELS[nluResult.intent],
      status: 'matched',
    });

    result.confidence = computeConfidence(result);
    return true;
  }

  private doParse(input: string, figli: Studente[], fuzzy: boolean): ParseResult {
    if (!input || !input.trim()) {
      return this.emptyResult();
    }

    // 1. Extract dates from lossless text (before stopword removal)
    const lightNormalized = normalizeLightInput(input);
    const dateExtraction = extractDates(lightNormalized, fuzzy);
    let data: ParseResult['data'] = null;

    // 2. Extract amount (after dates, before tokenize)
    const textAfterDates = dateExtraction ? dateExtraction.textWithPlaceholder : lightNormalized;
    const amountExtraction = extractAmount(textAfterDates);
    let importo: number | null = null;

    // 3. Tokenize (with date/amount segments replaced by placeholders)
    const textForTokenizer = amountExtraction ? amountExtraction.textWithPlaceholder : (dateExtraction ? dateExtraction.textWithPlaceholder : input);
    const { tokens, detectedRemoveIntent } = tokenize(textForTokenizer);
    const childNames = figli.map(f => f.nome ?? '');
    const parsedTokens: ParsedToken[] = [];
    const consumed = new Set<number>();

    // 4. Build date parsed token and mark __DATE__ as consumed
    if (dateExtraction) {
      data = dateExtraction.dateResult.match;
      const dateIdx = tokens.indexOf('__DATE__');
      if (dateIdx !== -1) consumed.add(dateIdx);

      const dateLabel = data.fine
        ? `${formatDateForDisplay(data.inizio)} - ${formatDateForDisplay(data.fine)}`
        : formatDateForDisplay(data.inizio);

      parsedTokens.push({
        raw: dateExtraction.dateResult.rawTokens.join(' '),
        type: 'data',
        resolved: dateLabel,
        status: 'matched',
      });
    }

    // 5. Build amount parsed token and mark __AMOUNT__ as consumed
    if (amountExtraction) {
      importo = amountExtraction.amountResult.value;
      const amountIdx = tokens.indexOf('__AMOUNT__');
      if (amountIdx !== -1) consumed.add(amountIdx);

      parsedTokens.push({
        raw: amountExtraction.amountResult.consumedRaw,
        type: 'importo',
        resolved: formatEuro(importo),
        status: 'matched',
      });
    }

    // 6. Match intent
    let intent: ActionType | null = null;
    const intentMatch = matchIntent(tokens, childNames, detectedRemoveIntent);
    if (intentMatch) {
      intent = intentMatch.intent;
      const idx = tokens.indexOf(intentMatch.matchedToken);
      if (idx !== -1) consumed.add(idx);

      const label = INTENT_LABELS[intent];
      parsedTokens.push({
        raw: intentMatch.matchedToken,
        type: 'intent',
        resolved: label,
        status: 'matched',
      });
    }

    // 7. Match child (on remaining unconsumed tokens)
    let childResult: ParseResult['childMatch'] = null;
    let figlio: Studente | null = null;
    let figlioAutoSelected = false;

    for (let i = 0; i < tokens.length; i++) {
      if (consumed.has(i)) continue;
      const token = tokens[i];

      const match = matchChild(token, figli, fuzzy);
      if (match) {
        childResult = match;
        figlio = match.studente;
        consumed.add(i);

        const resolvedName = `${match.studente.nome} ${match.studente.cognome}`.trim();
        parsedTokens.push({
          raw: token,
          type: 'figlio',
          resolved: resolvedName,
          status: match.status,
        });
        break;
      }
    }

    // Auto-select single child
    if (!figlio && figli.length === 1) {
      figlio = figli[0];
      figlioAutoSelected = true;
      childResult = { studente: figli[0], status: 'matched' };
    }

    // Mark remaining tokens as noise
    for (let i = 0; i < tokens.length; i++) {
      if (!consumed.has(i)) {
        parsedTokens.push({
          raw: tokens[i],
          type: 'noise',
          resolved: tokens[i],
          status: 'unmatched',
        });
      }
    }

    const partial = { intent, figlio, data, importo };
    const confidence = computeConfidence(partial);

    return { intent, figlio, figlioAutoSelected, childMatch: childResult, data, importo, confidence, tokens: parsedTokens };
  }

  private emptyResult(): ParseResult {
    return {
      intent: null,
      figlio: null,
      figlioAutoSelected: false,
      childMatch: null,
      data: null,
      importo: null,
      confidence: 'none',
      tokens: [],
    };
  }
}
